import { IResultsRepository } from './IResultsRepository';
import {
  ImportBatch,
  SnapshotDetail,
  CreateImportBatchInput,
  CreateImportBatchResult,
  ValidationError,
  ValidationMotivo,
  AccountDetailStagingRow,
  UpdateStagingRowInput,
  ListStagingFilters,
  AccountDetail,
  UpdateAccountDetailInput,
  ListAccountDetailFilters,
  PagedResult,
  SummaryFilters,
  SummaryRow,
  SummaryDetailFilters,
  SummaryDetailRow,
  ClientComparisonFilters,
  ClientComparisonRow,
  ClientMonthValue,
  AvailableMonth,
  ClientMapping,
  CreateClientMappingInput,
  UpdateClientMappingInput,
  AccountCatalogItem,
  CreateAccountInput,
  UpdateAccountInput,
  ServiceType,
  CreateServiceTypeInput,
  UpdateServiceTypeInput,
} from './types';

const BATCHES_KEY = 'lcc.results.batches';
const SNAPSHOT_KEY = 'lcc.results.snapshot';
const VALIDATION_KEY = 'lcc.results.validationErrors';
const STAGING_KEY = 'lcc.results.staging';
const FINAL_KEY = 'lcc.results.accountDetail';
const MAPPING_KEY = 'lcc.results.clientMappings';
const ACCOUNT_KEY = 'lcc.results.accounts';
const SERVICE_TYPE_KEY = 'lcc.results.serviceTypes';
const DEFAULT_SMALL_VALUE_THRESHOLD = 10_000;

const SEED_MAPPINGS: ClientMapping[] = [
  { id: crypto.randomUUID(), clienteDatosTabla: 'ADMINISTRATIVO', clienteResultadosMes: 'ADMINISTRATIVO', nombreCorto: 'ADMON', tipoServicio: 'ADMINISTRATIVO', isActive: true },
  { id: crypto.randomUUID(), clienteDatosTabla: 'COL BOGOTA', clienteResultadosMes: 'COLTABACO BOGOTA', nombreCorto: 'COLT BOG', tipoServicio: 'TRANSPORTE', isActive: true },
  { id: crypto.randomUUID(), clienteDatosTabla: 'VENTAS', clienteResultadosMes: 'VENTA', nombreCorto: 'VENTA', tipoServicio: 'COMERCIAL', isActive: true },
];
const SEED_SERVICE_TYPES: ServiceType[] = ['ADMINISTRATIVO', 'COMERCIAL', 'OP LOGISTICA', 'TRANSPORTE', 'ALM EXTERNO', 'RENTA MONTACARGAS', 'OTROS SERVICIOS'].map((nombre) => ({
  id: crypto.randomUUID(),
  nombre,
  isActive: true,
}));

function readOrSeed<T>(key: string, seed: T[]): T[] {
  const raw = localStorage.getItem(key);
  if (raw) return JSON.parse(raw);
  localStorage.setItem(key, JSON.stringify(seed));
  return seed;
}
function write<T>(key: string, data: T[]) {
  localStorage.setItem(key, JSON.stringify(data));
}
function norm(s: string): string {
  return s.trim().toUpperCase().replace(/\s+/g, ' ');
}

/** Implementación local (modo desarrollo/demo sin backend) del módulo Resultados Financieros. */
export class LocalResultsRepository implements IResultsRepository {
  async listBatches(): Promise<ImportBatch[]> {
    return readOrSeed<ImportBatch>(BATCHES_KEY, []).sort((a, b) => b.createdDate.localeCompare(a.createdDate));
  }
  async getBatch(id: string): Promise<ImportBatch | null> {
    return readOrSeed<ImportBatch>(BATCHES_KEY, []).find((b) => b.id === id) ?? null;
  }
  async listSnapshotDetail(importBatchId: string): Promise<SnapshotDetail[]> {
    return readOrSeed<SnapshotDetail & { importBatchId: string }>(SNAPSHOT_KEY, []).filter((s) => s.importBatchId === importBatchId);
  }
  async listValidationErrors(importBatchId: string): Promise<ValidationError[]> {
    return readOrSeed<ValidationError & { importBatchId: string }>(VALIDATION_KEY, []).filter((v) => v.importBatchId === importBatchId);
  }

  /** Misma lógica que el backend (ver ResultsRepository.createImportBatch): genera "Datos Tabla" desde la única hoja y valida "Por revisar" sobre ella misma. */
  async createImportBatch(input: CreateImportBatchInput): Promise<CreateImportBatchResult> {
    const accountRows = input.snapshotRows.filter((r) => r.rowType === 'ACCOUNT');
    const sectionTotalRows = input.snapshotRows.filter((r) => r.rowType === 'SECTION_TOTAL');
    const utilidadRow = input.snapshotRows.find((r) => r.rowType === 'UTILIDAD');
    const smallValueThreshold = input.smallValueThreshold ?? DEFAULT_SMALL_VALUE_THRESHOLD;

    const totalIngresos = sectionTotalRows.find((r) => r.tipoCuenta === 'INGRESO')?.values['TOTALES'] ?? accountRows.filter((r) => r.tipoCuenta === 'INGRESO').reduce((s, r) => s + (r.values['TOTALES'] ?? 0), 0);
    const totalGastos = sectionTotalRows.find((r) => r.tipoCuenta === 'GASTOS')?.values['TOTALES'] ?? accountRows.filter((r) => r.tipoCuenta === 'GASTOS').reduce((s, r) => s + (r.values['TOTALES'] ?? 0), 0);
    const utilidad = utilidadRow?.values['TOTALES'] ?? totalIngresos - totalGastos;

    const batch: ImportBatch = {
      id: crypto.randomUUID(),
      sourceMonth: input.sourceMonth,
      sourceYear: input.sourceYear,
      fileName: input.fileName,
      status: 'Cargado',
      totalIngresos,
      totalGastos,
      utilidad,
      validationErrorCount: 0,
      createdDate: new Date().toISOString(),
      createdBy: 'Demo local',
    };
    const batches = readOrSeed<ImportBatch>(BATCHES_KEY, []);
    batches.push(batch);
    write(BATCHES_KEY, batches);

    // --- snapshot completo (incluidas filas de control) ---
    const snapshotRows: (SnapshotDetail & { importBatchId: string })[] = [];
    for (const row of input.snapshotRows) {
      for (const [clientColumn, value] of Object.entries(row.values)) {
        snapshotRows.push({
          id: crypto.randomUUID(),
          importBatchId: batch.id,
          accountCode: row.accountCode,
          accountDescription: row.accountDescription,
          tipoCuenta: row.tipoCuenta,
          rowType: row.rowType,
          clientColumn,
          isTotalColumn: clientColumn === 'TOTALES',
          value,
        });
      }
    }
    const allSnapshots = readOrSeed<SnapshotDetail & { importBatchId: string }>(SNAPSHOT_KEY, []);
    write(SNAPSHOT_KEY, [...allSnapshots, ...snapshotRows]);

    // --- mapeo de clientes (columna del Excel -> Cliente "Datos Tabla" + Tipo de Servicio) ---
    const mappings = readOrSeed<ClientMapping>(MAPPING_KEY, SEED_MAPPINGS);
    const byResultadosMes = new Map(mappings.map((m) => [norm(m.clienteResultadosMes), m]));
    const clientColumns = Array.from(new Set(accountRows.flatMap((r) => Object.keys(r.values)))).filter((c) => c !== 'TOTALES');

    // --- generar "Datos Tabla" (staging) desde las filas de cuenta ---
    // Solo se guardan combinaciones cuenta+cliente con VALOR DISTINTO DE CERO.
    const stagingRows: AccountDetailStagingRow[] = [];
    for (const row of accountRows) {
      for (const clientColumn of clientColumns) {
        const valor = row.values[clientColumn] ?? 0;
        if (valor === 0) continue;
        const mapping = byResultadosMes.get(norm(clientColumn));
        stagingRows.push({
          id: crypto.randomUUID(),
          importBatchId: batch.id,
          mes: input.sourceMonth,
          anio: input.sourceYear,
          cliente: mapping?.clienteDatosTabla ?? clientColumn,
          tipoServicio: mapping?.tipoServicio ?? 'SIN CLASIFICAR',
          cuentaCodigo: row.accountCode,
          cuentaDescripcion: row.accountDescription,
          tipoCuenta: row.tipoCuenta ?? 'GASTOS',
          valor,
          status: 'No revisado',
        });
      }
    }
    const allStaging = readOrSeed<AccountDetailStagingRow>(STAGING_KEY, []);
    write(STAGING_KEY, [...allStaging, ...stagingRows]);

    // --- validación "Por revisar" sobre la propia hoja ---
    const errors: (ValidationError & { importBatchId: string })[] = [];
    const clientesSinTipo = new Set<string>();
    for (const clientColumn of clientColumns) {
      const mapping = byResultadosMes.get(norm(clientColumn));
      if (!mapping?.tipoServicio && !clientesSinTipo.has(clientColumn)) {
        clientesSinTipo.add(clientColumn);
        errors.push(makeError(batch.id, '—', null, clientColumn, null, null, null, 'CLIENTE_SIN_TIPO_SERVICIO'));
      }
    }
    for (const row of accountRows) {
      const rowValues = clientColumns.map((c) => row.values[c] ?? 0);
      if (rowValues.every((v) => v === 0)) {
        errors.push(makeError(batch.id, row.accountCode, row.accountDescription, null, 0, null, null, 'CUENTA_SIN_REPORTE'));
        continue;
      }
      for (const clientColumn of clientColumns) {
        const value = row.values[clientColumn] ?? 0;
        if (value < 0) errors.push(makeError(batch.id, row.accountCode, row.accountDescription, clientColumn, value, null, null, 'VALOR_NEGATIVO'));
        else if (value > 0 && value < smallValueThreshold) errors.push(makeError(batch.id, row.accountCode, row.accountDescription, clientColumn, value, null, null, 'VALOR_MUY_PEQUENO'));
      }
    }
    for (const tipo of ['INGRESO', 'GASTOS'] as const) {
      const fileRow = sectionTotalRows.find((r) => r.tipoCuenta === tipo);
      if (!fileRow) continue;
      const label = tipo === 'INGRESO' ? 'TOTAL INGRESOS' : 'TOTAL EGRESOS';
      for (const clientColumn of clientColumns) {
        const computed = accountRows.filter((r) => r.tipoCuenta === tipo).reduce((s, r) => s + (r.values[clientColumn] ?? 0), 0);
        const fileValue = fileRow.values[clientColumn] ?? 0;
        const diff = Math.abs(computed - fileValue);
        if (diff > 1) errors.push(makeError(batch.id, label, null, clientColumn, computed, fileValue, diff, 'TOTAL_NO_CUADRA'));
      }
    }
    if (utilidadRow) {
      for (const clientColumn of clientColumns) {
        const ing = accountRows.filter((r) => r.tipoCuenta === 'INGRESO').reduce((s, r) => s + (r.values[clientColumn] ?? 0), 0);
        const gas = accountRows.filter((r) => r.tipoCuenta === 'GASTOS').reduce((s, r) => s + (r.values[clientColumn] ?? 0), 0);
        const computed = ing - gas;
        const fileValue = utilidadRow.values[clientColumn] ?? 0;
        const diff = Math.abs(computed - fileValue);
        if (diff > 1) errors.push(makeError(batch.id, 'UTILIDAD', null, clientColumn, computed, fileValue, diff, 'TOTAL_NO_CUADRA'));
      }
    }

    const allErrors = readOrSeed<ValidationError & { importBatchId: string }>(VALIDATION_KEY, []);
    write(VALIDATION_KEY, [...allErrors, ...errors]);
    if (errors.length > 0) {
      batch.validationErrorCount = errors.length;
      write(BATCHES_KEY, readOrSeed<ImportBatch>(BATCHES_KEY, []).map((b) => (b.id === batch.id ? batch : b)));
    }

    return { batch, validationErrors: errors };
  }

  /** Mismo criterio que el backend: borra todo lo del mes (batch, snapshot, staging, errores) y deja "borrado" lo del histórico final. */
  async deleteMonthProcessing(importBatchId: string): Promise<void> {
    const batches = readOrSeed<ImportBatch>(BATCHES_KEY, []);
    const batch = batches.find((b) => b.id === importBatchId);
    if (!batch) return;
    const batchIds = new Set(batches.filter((b) => b.sourceMonth === batch.sourceMonth && b.sourceYear === batch.sourceYear).map((b) => b.id));

    write(BATCHES_KEY, batches.filter((b) => !batchIds.has(b.id)));
    write(SNAPSHOT_KEY, readOrSeed<SnapshotDetail & { importBatchId: string }>(SNAPSHOT_KEY, []).filter((s) => !batchIds.has(s.importBatchId)));
    write(VALIDATION_KEY, readOrSeed<ValidationError & { importBatchId: string }>(VALIDATION_KEY, []).filter((v) => !batchIds.has(v.importBatchId)));
    write(STAGING_KEY, readOrSeed<AccountDetailStagingRow>(STAGING_KEY, []).filter((s) => !batchIds.has(s.importBatchId)));
    write(FINAL_KEY, readOrSeed<AccountDetail>(FINAL_KEY, []).filter((r) => !(r.mes === batch.sourceMonth && r.anio === batch.sourceYear)));
  }

  async listAvailableMonths(): Promise<AvailableMonth[]> {
    const batches = readOrSeed<ImportBatch>(BATCHES_KEY, []);
    const staging = readOrSeed<AccountDetailStagingRow>(STAGING_KEY, []);
    return batches.map((b) => ({
      mes: b.sourceMonth,
      anio: b.sourceYear,
      batchStatus: b.status,
      pendingStagingCount: staging.filter((s) => s.mes === b.sourceMonth && s.anio === b.sourceYear && s.status !== 'Procesado').length,
    }));
  }

  async listStaging(filters: ListStagingFilters): Promise<AccountDetailStagingRow[]> {
    return readOrSeed<AccountDetailStagingRow>(STAGING_KEY, []).filter(
      (s) =>
        (!filters.importBatchId || s.importBatchId === filters.importBatchId) &&
        (!filters.mes || s.mes === filters.mes) &&
        (!filters.anio || s.anio === filters.anio) &&
        (!filters.status || s.status === filters.status)
    );
  }
  async updateStagingRow(id: string, patch: UpdateStagingRowInput): Promise<AccountDetailStagingRow> {
    const rows = readOrSeed<AccountDetailStagingRow>(STAGING_KEY, []);
    const updated = rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
    write(STAGING_KEY, updated);
    return updated.find((r) => r.id === id)!;
  }
  async deleteStagingRow(id: string): Promise<void> {
    write(STAGING_KEY, readOrSeed<AccountDetailStagingRow>(STAGING_KEY, []).filter((r) => r.id !== id));
  }
  async confirmMonth(mes: string, anio: number): Promise<number> {
    const staging = readOrSeed<AccountDetailStagingRow>(STAGING_KEY, []);
    const toConfirm = staging.filter((s) => s.mes === mes && s.anio === anio);
    if (toConfirm.length === 0) return 0;
    const final = readOrSeed<AccountDetail>(FINAL_KEY, []);
    for (const row of toConfirm) {
      const idx = final.findIndex((f) => f.mes === mes && f.anio === anio && f.cliente === row.cliente && f.cuentaCodigo === row.cuentaCodigo);
      const dto: AccountDetail = { id: idx >= 0 ? final[idx].id : crypto.randomUUID(), mes, anio, cliente: row.cliente, tipoServicio: row.tipoServicio, cuentaCodigo: row.cuentaCodigo, cuentaDescripcion: row.cuentaDescripcion, tipoCuenta: row.tipoCuenta, valor: row.valor };
      if (idx >= 0) final[idx] = dto;
      else final.push(dto);
    }
    write(FINAL_KEY, final);
    write(
      STAGING_KEY,
      staging.map((s) => (s.mes === mes && s.anio === anio ? { ...s, status: 'Procesado' as const } : s))
    );
    write(
      BATCHES_KEY,
      readOrSeed<ImportBatch>(BATCHES_KEY, []).map((b) => (b.sourceMonth === mes && b.sourceYear === anio ? { ...b, status: 'Procesado' as const } : b))
    );
    return toConfirm.length;
  }

  async listAccountDetail(filters: ListAccountDetailFilters): Promise<PagedResult<AccountDetail>> {
    const all = readOrSeed<AccountDetail>(FINAL_KEY, []).filter(
      (r) =>
        (!filters.mes || r.mes === filters.mes) &&
        (!filters.anio || r.anio === filters.anio) &&
        (!filters.cliente || r.cliente === filters.cliente) &&
        (!filters.tipoServicio || r.tipoServicio === filters.tipoServicio) &&
        (filters.includeEmpty || r.valor !== 0)
    );
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 10;
    const start = (page - 1) * pageSize;
    return { items: all.slice(start, start + pageSize), total: all.length, page, pageSize };
  }
  async updateAccountDetail(id: string, patch: UpdateAccountDetailInput): Promise<AccountDetail> {
    const rows = readOrSeed<AccountDetail>(FINAL_KEY, []);
    const updated = rows.map((r) => (r.id === id ? { ...r, ...patch } : r));
    write(FINAL_KEY, updated);
    return updated.find((r) => r.id === id)!;
  }
  async deleteAccountDetail(id: string): Promise<void> {
    write(FINAL_KEY, readOrSeed<AccountDetail>(FINAL_KEY, []).filter((r) => r.id !== id));
  }

  async getSummary(filters: SummaryFilters): Promise<SummaryRow[]> {
    const rows = readOrSeed<AccountDetail>(FINAL_KEY, []).filter(
      (r) =>
        r.anio === filters.anio &&
        (!filters.tiposServicio || filters.tiposServicio.length === 0 || filters.tiposServicio.includes(r.tipoServicio)) &&
        (!filters.clientes || filters.clientes.length === 0 || filters.clientes.includes(r.cliente))
    );
    const byTipo = new Map<string, Record<string, number>>();
    for (const r of rows) {
      const bucket = byTipo.get(r.tipoCuenta) ?? {};
      bucket[r.mes] = (bucket[r.mes] ?? 0) + r.valor;
      byTipo.set(r.tipoCuenta, bucket);
    }
    return Array.from(byTipo.entries()).map(([tipoCuenta, valuesByMonth]) => ({
      tipoCuenta: tipoCuenta as SummaryRow['tipoCuenta'],
      valuesByMonth,
      total: Object.values(valuesByMonth).reduce((a, b) => a + b, 0),
    }));
  }

  async getSummaryDetail(filters: SummaryDetailFilters): Promise<SummaryDetailRow[]> {
    const rows = readOrSeed<AccountDetail>(FINAL_KEY, []).filter(
      (r) =>
        r.anio === filters.anio &&
        r.tipoCuenta === filters.tipoCuenta &&
        (!filters.tiposServicio || filters.tiposServicio.length === 0 || filters.tiposServicio.includes(r.tipoServicio)) &&
        (!filters.clientes || filters.clientes.length === 0 || filters.clientes.includes(r.cliente))
    );
    const byCuenta = new Map<string, { cuentaDescripcion: string; valuesByMonth: Record<string, number> }>();
    for (const r of rows) {
      const bucket = byCuenta.get(r.cuentaCodigo) ?? { cuentaDescripcion: r.cuentaDescripcion, valuesByMonth: {} };
      bucket.valuesByMonth[r.mes] = (bucket.valuesByMonth[r.mes] ?? 0) + r.valor;
      byCuenta.set(r.cuentaCodigo, bucket);
    }
    return Array.from(byCuenta.entries())
      .map(([cuentaCodigo, { cuentaDescripcion, valuesByMonth }]) => ({ cuentaCodigo, cuentaDescripcion, valuesByMonth, total: Object.values(valuesByMonth).reduce((a, b) => a + b, 0) }))
      .sort((a, b) => b.total - a.total);
  }

  async getClientComparison(filters: ClientComparisonFilters): Promise<ClientComparisonRow[]> {
    const rows = readOrSeed<AccountDetail>(FINAL_KEY, []).filter(
      (r) =>
        r.anio === filters.anio &&
        (!filters.tiposServicio || filters.tiposServicio.length === 0 || filters.tiposServicio.includes(r.tipoServicio)) &&
        (!filters.clientes || filters.clientes.length === 0 || filters.clientes.includes(r.cliente)) &&
        (!filters.meses || filters.meses.length === 0 || filters.meses.includes(r.mes))
    );
    const byCliente = new Map<string, Record<string, { ingreso: number; gastos: number }>>();
    for (const r of rows) {
      const meses = byCliente.get(r.cliente) ?? {};
      const bucket = meses[r.mes] ?? { ingreso: 0, gastos: 0 };
      if (r.tipoCuenta === 'INGRESO') bucket.ingreso += r.valor;
      else bucket.gastos += Math.abs(r.valor);
      meses[r.mes] = bucket;
      byCliente.set(r.cliente, meses);
    }
    return Array.from(byCliente.entries())
      .map(([cliente, meses]): ClientComparisonRow => {
        const valuesByMonth: Record<string, ClientMonthValue> = {};
        let totalIngreso = 0;
        let totalGastos = 0;
        for (const [mes, { ingreso, gastos }] of Object.entries(meses)) {
          valuesByMonth[mes] = { ingreso, gastos, utilidad: ingreso - gastos };
          totalIngreso += ingreso;
          totalGastos += gastos;
        }
        return { cliente, valuesByMonth, totalIngreso, totalGastos, totalUtilidad: totalIngreso - totalGastos };
      })
      .sort((a, b) => b.totalUtilidad - a.totalUtilidad);
  }

  async listDistinctClients(): Promise<string[]> {
    return Array.from(new Set(readOrSeed<AccountDetail>(FINAL_KEY, []).map((r) => r.cliente))).sort();
  }

  async listClientMappings(): Promise<ClientMapping[]> {
    return readOrSeed<ClientMapping>(MAPPING_KEY, SEED_MAPPINGS);
  }
  async createClientMapping(input: CreateClientMappingInput): Promise<ClientMapping> {
    const rows = readOrSeed<ClientMapping>(MAPPING_KEY, SEED_MAPPINGS);
    const row: ClientMapping = { id: crypto.randomUUID(), ...input };
    write(MAPPING_KEY, [...rows, row]);
    return row;
  }
  async updateClientMapping(id: string, patch: UpdateClientMappingInput): Promise<ClientMapping> {
    const rows = readOrSeed<ClientMapping>(MAPPING_KEY, SEED_MAPPINGS).map((r) => (r.id === id ? { ...r, ...patch } : r));
    write(MAPPING_KEY, rows);
    return rows.find((r) => r.id === id)!;
  }
  async deleteClientMapping(id: string): Promise<void> {
    write(MAPPING_KEY, readOrSeed<ClientMapping>(MAPPING_KEY, SEED_MAPPINGS).filter((r) => r.id !== id));
  }

  async listAccounts(): Promise<AccountCatalogItem[]> {
    return readOrSeed<AccountCatalogItem>(ACCOUNT_KEY, []);
  }
  async createAccount(input: CreateAccountInput): Promise<AccountCatalogItem> {
    const rows = readOrSeed<AccountCatalogItem>(ACCOUNT_KEY, []);
    const row: AccountCatalogItem = { id: crypto.randomUUID(), ...input };
    write(ACCOUNT_KEY, [...rows, row]);
    return row;
  }
  async updateAccount(id: string, patch: UpdateAccountInput): Promise<AccountCatalogItem> {
    const rows = readOrSeed<AccountCatalogItem>(ACCOUNT_KEY, []).map((r) => (r.id === id ? { ...r, ...patch } : r));
    write(ACCOUNT_KEY, rows);
    return rows.find((r) => r.id === id)!;
  }
  async deleteAccount(id: string): Promise<void> {
    write(ACCOUNT_KEY, readOrSeed<AccountCatalogItem>(ACCOUNT_KEY, []).filter((r) => r.id !== id));
  }

  async listServiceTypes(): Promise<ServiceType[]> {
    return readOrSeed<ServiceType>(SERVICE_TYPE_KEY, SEED_SERVICE_TYPES);
  }
  async createServiceType(input: CreateServiceTypeInput): Promise<ServiceType> {
    const rows = readOrSeed<ServiceType>(SERVICE_TYPE_KEY, SEED_SERVICE_TYPES);
    const row: ServiceType = { id: crypto.randomUUID(), ...input };
    write(SERVICE_TYPE_KEY, [...rows, row]);
    return row;
  }
  async updateServiceType(id: string, patch: UpdateServiceTypeInput): Promise<ServiceType> {
    const rows = readOrSeed<ServiceType>(SERVICE_TYPE_KEY, SEED_SERVICE_TYPES).map((r) => (r.id === id ? { ...r, ...patch } : r));
    write(SERVICE_TYPE_KEY, rows);
    return rows.find((r) => r.id === id)!;
  }
  async deleteServiceType(id: string): Promise<void> {
    write(SERVICE_TYPE_KEY, readOrSeed<ServiceType>(SERVICE_TYPE_KEY, SEED_SERVICE_TYPES).filter((r) => r.id !== id));
  }
}

function makeError(
  importBatchId: string,
  cuentaCodigo: string,
  cuentaDescripcion: string | null,
  cliente: string | null,
  valor: number | null,
  valorEsperado: number | null,
  diferencia: number | null,
  motivo: ValidationMotivo
): ValidationError & { importBatchId: string } {
  return { id: crypto.randomUUID(), importBatchId, cuentaCodigo, cuentaDescripcion, cliente, valor, valorEsperado, diferencia, motivo };
}
